import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
    Dialog,
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import FlashlightOffIcon from '@mui/icons-material/FlashlightOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import { isBarcodeScanSupported } from '../../../utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { White, Black, darkGray, green, red } from '../../../color_utils';
import { logScanEvent } from '../../../Utils/scan_utils';

const SCAN_FORMATS = [
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.QR_CODE,
];

const NO_DECODE_TIMEOUT_MS = 30000;

let scannerSeq = 0;

const BarcodeScanner = ({
    open,
    onClose,
    onDecode,
    title,
    source,
    sourceContext,
    resultNode,
    onScanAgain,
}) => {
    const [phase, setPhase] = useState('starting');
    const [torchOn, setTorchOn] = useState(false);
    const [torchSupported, setTorchSupported] = useState(false);
    const [manualValue, setManualValue] = useState('');

    const message = {
        denied: TranslateW('ScanCameraPermissionDenied'),
        unsupported: TranslateW('ScanDeviceBrowserNotSupported'),
        timeout: TranslateW('ScanTimeoutRetry'),
        hint: TranslateW('ScanPointAtBarcode'),
        flashOn: TranslateW('ScanFlashOn'),
        flashOff: TranslateW('ScanFlashOff'),
        cancel: TranslateW('ScanCancel'),
        tryAgain: TranslateW('ScanTryAgain'),
        scanAgain: TranslateW('ScanAgain'),
        typeToSearch: TranslateW('ScanTypeToSearch'),
    };

    const containerIdRef = useRef(`barcode-scanner-${++scannerSeq}`);
    const html5QrcodeRef = useRef(null);
    const timeoutRef = useRef(null);
    const decodedRef = useRef(false);

    const clearTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const stopCamera = useCallback(async () => {
        clearTimer();
        const instance = html5QrcodeRef.current;
        html5QrcodeRef.current = null;
        if (!instance) return;
        try {
            if (instance.isScanning) {
                await instance.stop();
            }
            instance.clear();
        } catch {
            console.error('Error occurred while stopping camera');
        }
    }, []);

    const handleDecoded = useCallback(async (value) => {
        if (decodedRef.current) return;
        decodedRef.current = true;
        clearTimer();
        await stopCamera();
        logScanEvent({ source, sourceContext, isScanSuccess: true, value });
        onDecode(value);
    }, [onDecode, source, sourceContext, stopCamera]);

    const startCamera = useCallback(async () => {
        decodedRef.current = false;
        setPhase('starting');
        setTorchOn(false);

        const instance = new Html5Qrcode(containerIdRef.current, {
            formatsToSupport: SCAN_FORMATS,
            verbose: false,
        });
        html5QrcodeRef.current = instance;

        try {
            await instance.start(
                { facingMode: 'environment' },
                { fps: 10 },
                (decodedText) => { handleDecoded(decodedText); },
                () => {},
            );
            setPhase('scanning');

            try {
                const caps = instance.getRunningTrackCapabilities?.();
                setTorchSupported(!!(caps && caps.torch));
            } catch {
                setTorchSupported(false);
            }

            timeoutRef.current = setTimeout(() => {
                if (!decodedRef.current) {
                    stopCamera();
                    logScanEvent({ source, sourceContext, isScanSuccess: false, value: '(empty)' });
                    setPhase('timeout');
                }
            }, NO_DECODE_TIMEOUT_MS);
        } catch {
            await stopCamera();
            logScanEvent({ source, sourceContext, isScanSuccess: false, value: '(empty)' });
            setPhase('denied');
            }
    }, [handleDecoded, source, sourceContext, stopCamera]);

    // Open / close lifecycle.
    useEffect(() => {
        if (!open) return undefined;

        setManualValue('');
        if (!isBarcodeScanSupported()) {
            logScanEvent({ source, sourceContext, isScanSuccess: false, value: '(empty)' });
            setPhase('unsupported');
            return undefined;
        }
        // Wait a frame so the target container is in the DOM before html5-qrcode attaches.
        const raf = requestAnimationFrame(() => { startCamera(); });
        return () => {
            cancelAnimationFrame(raf);
            stopCamera();
        };
    }, [open, startCamera, stopCamera, source, sourceContext]);

    const toggleTorch = async () => {
        const instance = html5QrcodeRef.current;
        if (!instance) return;
        const next = !torchOn;
        try {
            await instance.applyVideoConstraints({ advanced: [{ torch: next }] });
            setTorchOn(next);
        } catch {
            setTorchSupported(false);
        }
    };

    const handleClose = () => {
        stopCamera();
        onClose();
    };

    const submitManual = () => {
        const value = manualValue.trim();
        if (!value) return;
        handleDecoded(value);
    };


    const scanAgain = () => {
        onScanAgain();
        setManualValue('');
        startCamera();
    };

    const showCamera = phase === 'starting' || phase === 'scanning';
    const showError = phase === 'denied' || phase === 'unsupported' || phase === 'timeout';

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { backgroundColor: Black, color: White, display: 'flex', flexDirection: 'column' } }}
        >
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1, backgroundColor: darkGray, flexShrink: 0,
            }}>
                <Typography sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.1 }}>
                    {title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {showCamera && torchSupported && (
                        <Button
                            onClick={toggleTorch}
                            startIcon={torchOn ? <FlashlightOnIcon /> : <FlashlightOffIcon />}
                            sx={{ color: White, fontSize: '12px' }}
                        >
                            {torchOn ? message.flashOn : message.flashOff}
                        </Button>
                    )}
                    <Button onClick={handleClose} sx={{ color: White, fontSize: '12px' }}>
                        {message.cancel}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden', backgroundColor: Black }}>
                <Box
                    id={containerIdRef.current}
                    sx={{
                        position: 'absolute', inset: 0,
                        '& video': { width: '100% !important', height: '100% !important', objectFit: 'cover' },
                    }}
                />
                {showCamera && (
                    <>
                        <Box sx={{
                            position: 'absolute', top: '45%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '70%', maxWidth: 300, aspectRatio: '1 / 1',
                            pointerEvents: 'none', overflow: 'hidden',
                            backgroundImage: `
                                linear-gradient(${green},${green}),linear-gradient(${green},${green}),
                                linear-gradient(${green},${green}),linear-gradient(${green},${green}),
                                linear-gradient(${green},${green}),linear-gradient(${green},${green}),
                                linear-gradient(${green},${green}),linear-gradient(${green},${green})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '4px 36px, 36px 4px',
                            backgroundPosition:
                                'top left, top left, top right, top right, bottom left, bottom left, bottom right, bottom right',
                        }}>
                            {/* Animated red scan line */}
                            <Box sx={{
                                position: 'absolute', left: 0, right: 0, height: '2px',
                                backgroundColor: red, boxShadow: `0 0 8px ${red}`,
                                animation: 'trakrefScanLine 2s ease-in-out infinite',
                                '@keyframes trakrefScanLine': {
                                    '0%': { top: '6%' },
                                    '50%': { top: '94%' },
                                    '100%': { top: '6%' },
                                },
                            }} />
                        </Box>
                        <Typography sx={{
                            position: 'absolute', bottom: 90, left: 0, right: 0,
                            textAlign: 'center', fontSize: '15px', color: White,
                        }}>
                            {message.hint}
                        </Typography>
                    </>
                )}

                {showError && (
                    <Box sx={{
                        position: 'absolute', inset: 0, backgroundColor: Black,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', px: 3, textAlign: 'center',
                    }}>
                        {/* Red warning icon on a white circle */}
                        <Box sx={{
                            width: 56, height: 56, borderRadius: '50%', backgroundColor: White,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                        }}>
                            <ErrorOutlineIcon sx={{ fontSize: 34, color: red }} />
                        </Box>
                        <Typography sx={{ mb: 2, fontSize: '15px', maxWidth: 360 }}>
                            {message[phase]}
                        </Typography>
                        {phase !== 'unsupported' && (
                            <Button
                                variant="outlined"
                                onClick={scanAgain}
                                sx={{ color: White, borderColor: White }}
                            >
                                {message.tryAgain}
                            </Button>
                        )}
                    </Box>
                )}

                {resultNode && (
                    <Box sx={{
                        position: 'absolute', inset: 0, backgroundColor: Black,
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        px: 3, overflowY: 'auto',
                    }}>
                        <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
                            {resultNode}
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={scanAgain}
                                sx={{ mt: 2, color: White, borderColor: White }}
                            >
                                {message.scanAgain}
                            </Button>
                        </Box>
                    </Box>
                )}

                {!resultNode && (
                    <Box sx={{ position: 'absolute', bottom: 24, left: 0, right: 0, px: 3 }}>
                        <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={message.typeToSearch}
                                value={manualValue}
                                onChange={(e) => setManualValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') submitManual(); }}
                                variant="outlined"
                                sx={{
                                    backgroundColor: White, borderRadius: 1,
                                    '& .MuiInputBase-input': { color: Black },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={submitManual} edge="end">
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

BarcodeScanner.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onDecode: PropTypes.func.isRequired,
    title: PropTypes.string,
    source: PropTypes.oneOf(['inventory-scan', 'service-event-scan']).isRequired,
    sourceContext: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resultNode: PropTypes.node,
    onScanAgain: PropTypes.func,
};

BarcodeScanner.defaultProps = {
    title: 'Scan barcode',
    sourceContext: undefined,
    resultNode: null,
    onScanAgain: () => {},
};

export default BarcodeScanner;
